import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** True while the main drawer is animating shut but `open` has not been cleared yet. */
  #closing = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    // `.menu-open` is the single source of truth: it is what CSS shows the drawer by, and
    // open() now sets it in the same task as the `open` attribute, so the two cannot
    // disagree. A close still running its animation counts as closed, because `open`
    // lingers for up to 400ms after the drawer has visually gone.
    return !this.#closing && this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    // <details> flips `open` itself as the click's default action, which runs AFTER
    // this handler. Combined with close()'s async reset() that left the drawer in
    // open=true + no .menu-open (visible in the DOM, hidden by CSS) and the menu
    // stopped opening from the second click on. Own the state here instead.
    event?.preventDefault?.();
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Abandon any close still waiting on its animation: its callback checks the class
    // and will bail out now that this open has set it again.
    this.#closing = false;

    details.open = true;
    summary.setAttribute('aria-expanded', 'true');

    // Reading a layout property flushes the pending style, so the browser has the closed
    // state to animate FROM before the class lands. This used to be a
    // requestAnimationFrame, which left the drawer `open` in the DOM but hidden by CSS
    // until the next frame, and indefinitely when frames are throttled. A click in that
    // gap saw isOpen === true with nothing on screen and closed an invisible drawer,
    // which is the click users had to press twice.
    void details.offsetHeight;
    details.classList.add('menu-open');

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    if (isMainDrawer) this.#closing = true;

    let done = false;

    // Runs once, from whichever of the two triggers fires first. onAnimationEnd alone was
    // unreliable: it can settle late or never, which is what left the drawer
    // open-but-hidden and unclickable. The class check is the whole guard now: if the
    // drawer was reopened while this close was animating, `.menu-open` is back and there
    // is nothing to tidy up.
    const finish = () => {
      if (done) return;
      if (details.classList.contains('menu-open')) return;

      done = true;
      if (isMainDrawer) this.#closing = false;
      reset(details);

      if (isMainDrawer) {
        removeTrapFocus();
        this.querySelectorAll('details[open]').forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(details, finish);
    setTimeout(finish, 400);
  }


  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
